import type { components } from "./api.gen";

/**
 * The live stream (EP10-S22, `10/05` §3).
 *
 * One stream per tab, read with `fetch` rather than `EventSource` so the token
 * travels in a header, never in the URL. An event is a hint: the screen
 * re-reads the entity over REST, so a missed event costs freshness, never
 * correctness. The React half (invalidation, the 60 s fallback poll, the
 * presence heartbeat) is `@khazzin/ui/patterns/live`.
 */

export type StreamEvent = components["schemas"]["StreamEvent"];

/** `idle`: no stream for this session; `open`: connected; `error`: reconnecting. */
export type StreamStatus = "idle" | "open" | "error";

/** What the server says when it could not replay everything missed. */
export const TOPIC_RESYNC = "stream.resync";

export interface StreamHandlers {
	onEvent: (event: StreamEvent) => void;
	onStatus: (status: StreamStatus) => void;
}

/** Opens one connection, resuming after `lastEventId` when there is one. */
export type OpenStream = (lastEventId: string | null, signal: AbortSignal) => Promise<Response>;

/** The wait before the nth reconnect after a failure: 1 s doubling to 30 s. */
export function backoff(failures: number): number {
	return Math.min(30_000, 1000 * 2 ** (failures - 1));
}

/** A pause that an abort ends early. */
function pause(ms: number, signal: AbortSignal): Promise<void> {
	return new Promise((resolve) => {
		const timer = setTimeout(resolve, ms);
		signal.addEventListener(
			"abort",
			() => {
				clearTimeout(timer);
				resolve();
			},
			{ once: true },
		);
	});
}

/**
 * Parses Server-Sent Events frames out of a byte stream, handing each `data:`
 * to `onFrame` with its `id:` (or null). Comments and `retry:` are skipped.
 */
export async function readFrames(
	body: ReadableStream<Uint8Array>,
	onFrame: (data: string, id: string | null) => void,
): Promise<void> {
	const reader = body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	for (;;) {
		const { value, done } = await reader.read();
		if (done) {
			return;
		}
		buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
		let end = buffer.indexOf("\n\n");
		while (end !== -1) {
			const frame = buffer.slice(0, end);
			buffer = buffer.slice(end + 2);
			end = buffer.indexOf("\n\n");
			let id: string | null = null;
			const data: string[] = [];
			for (const line of frame.split("\n")) {
				if (line.startsWith("id:")) {
					id = line.slice(3).trim();
				} else if (line.startsWith("data:")) {
					data.push(line.slice(5).trimStart());
				}
			}
			if (data.length > 0) {
				onFrame(data.join("\n"), id);
			}
		}
	}
}

/**
 * Keeps a stream open until the returned function is called: reconnects a
 * second after a clean end (Cloud Run closes a request after an hour), backs off
 * after a failure, and resumes with the last id it received. A 4xx other than
 * 429 means there is no stream for this session, and it stops.
 */
export function connectEventStream(
	open: OpenStream,
	{ onEvent, onStatus }: StreamHandlers,
): () => void {
	const control = new AbortController();
	const { signal } = control;
	let last: string | null = null;

	void (async () => {
		let failures = 0;
		while (!signal.aborted) {
			try {
				const response = await open(last, signal);
				if (response.status >= 400 && response.status < 500 && response.status !== 429) {
					onStatus("idle");
					return;
				}
				if (!response.ok || response.body === null) {
					throw new Error(`stream answered ${response.status}`);
				}
				failures = 0;
				onStatus("open");
				await readFrames(response.body, (data, id) => {
					if (id !== null) {
						last = id;
					}
					onEvent(JSON.parse(data) as StreamEvent);
				});
				// A clean end: reconnect after a second, without a status change.
				await pause(1000, signal);
			} catch {
				if (signal.aborted) {
					return;
				}
				failures += 1;
				onStatus("error");
				await pause(backoff(failures), signal);
			}
		}
	})();

	return () => control.abort();
}
